import { useEffect, useState } from 'react';
import api from '../api/apiClient';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import SortFilter, { sortItems } from '../components/SortFilter';

const emptyStudent = {
  full_name: '',
  student_id: '',
  email: '',
  parent_id: '',
  date_of_birth: '',
  date_of_admission: '',
  gender: '',
  class_grade: '',
  class_id: '',
  branch_id: '',
  blood_group: '',
  height: '',
  weight: '',
  primary_language: '',
  is_active: true,
};

function StudentAvatarCell({ src, name, size = 32 }) {
  const [error, setError] = useState(false);
  const initial = (name || 'S').charAt(0).toUpperCase();

  useEffect(() => {
    setError(false);
  }, [src]);

  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#ffffff', fontSize: Math.floor(size * 0.44), fontWeight: 700,
      flexShrink: 0, overflow: 'hidden', border: '2px solid #e0e7ff',
      boxShadow: '0 2px 6px rgba(99, 102, 241, 0.2)',
      textTransform: 'uppercase'
    }}>
      {src && !error ? (
        <img
          src={src}
          alt=""
          onError={() => setError(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        initial
      )}
    </div>
  );
}

const getMaxDobDate = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 2);
  return d.toISOString().split('T')[0];
};

const generateStudentPassword = (fullName, dateOfBirth) => {
  if (!fullName) return 'School@1234';
  const cleanName = String(fullName).replace(/[^a-zA-Z]/g, '').toUpperCase();
  const namePart = cleanName.slice(0, 4).padEnd(4, 'X');

  let dobPart = '';
  if (dateOfBirth) {
    const parts = String(dateOfBirth).split('T')[0].split('-');
    if (parts.length === 3) {
      const month = parts[1];
      const day = parts[2];
      dobPart = `${day}${month}`;
    }
  }

  return dobPart ? `${namePart}${dobPart}` : `${namePart}1234`;
};

export default function StudentsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentBranch, user } = useAuth();

  const [students, setStudents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [parents, setParents] = useState([]);
  const [sortOption, setSortOption] = useState('newest');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [form, setForm] = useState(emptyStudent);
  const [editingStudent, setEditingStudent] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [formClasses, setFormClasses] = useState([]);
  const [formClassesLoading, setFormClassesLoading] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(() => {
    if (currentBranch) {
      return currentBranch.branch_id;
    }
    return searchParams.get('branch_id') || '';
  });
  const [selectedClass, setSelectedClass] = useState('');
  const [filterClasses, setFilterClasses] = useState([]);
  const [filterClassesLoading, setFilterClassesLoading] = useState(false);

  // Dynamic Academic Years generator
  const getDynamicYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear - 1; y <= currentYear + 4; y++) {
      years.push(String(y));
    }
    return years;
  };

  // Promotion modal state
  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);
  const [promotionType, setPromotionType] = useState('december_annual');
  const [academicYear, setAcademicYear] = useState(() => String(new Date().getFullYear()));
  const [executionMode, setExecutionMode] = useState('scheduled');
  const [effectiveDate, setEffectiveDate] = useState(() => `${Number(new Date().getFullYear()) + 1}-01-01`);
  const [eligibleStudents, setEligibleStudents] = useState([]);
  const [promotionClasses, setPromotionClasses] = useState([]);
  const [selectedPromotionStudentIds, setSelectedPromotionStudentIds] = useState([]);
  const [targetClassMap, setTargetClassMap] = useState({});
  const [promotionLoading, setPromotionLoading] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);

  useEffect(() => {
    if (promotionType === 'june_mid_year') {
      setExecutionMode('immediate');
      setEffectiveDate(new Date().toISOString().split('T')[0]);
    } else {
      setExecutionMode('scheduled');
      const nextYr = Number(academicYear) || new Date().getFullYear();
      setEffectiveDate(`${nextYr + 1}-01-01`);
    }
  }, [promotionType, academicYear]);

  useEffect(() => {
    if (isPromoteModalOpen) {
      loadPromotionEligibility();
    }
  }, [isPromoteModalOpen, promotionType, selectedBranch, selectedClass]);

  async function loadPromotionEligibility() {
    setPromotionLoading(true);
    try {
      const activeBranchId = user?.role === 'admin' 
        ? selectedBranch 
        : (currentBranch?.branch_id || user?.branch_id || selectedBranch);

      const params = { promotion_type: promotionType };
      if (activeBranchId) params.branch_id = activeBranchId;
      if (selectedClass) params.class_id = selectedClass;

      const res = await api.get('/students/promotion/eligibility', { params });
      const studentsList = res.data.data || [];
      const classesList = res.data.classes || [];

      setEligibleStudents(studentsList);
      setPromotionClasses(classesList);

      const initialTargets = {};
      const initialSelected = [];
      studentsList.forEach(s => {
        if (s.recommended_target_class_id) {
          initialTargets[s.id] = String(s.recommended_target_class_id);
        }
        if (s.is_eligible) {
          initialSelected.push(s.id);
        }
      });
      setTargetClassMap(initialTargets);
      setSelectedPromotionStudentIds(initialSelected);
    } catch (err) {
      setEligibleStudents([]);
    } finally {
      setPromotionLoading(false);
    }
  }

  const toggleSelectAllPromotion = (checked) => {
    if (checked) {
      setSelectedPromotionStudentIds(eligibleStudents.filter(s => s.is_eligible).map(s => s.id));
    } else {
      setSelectedPromotionStudentIds([]);
    }
  };

  const toggleStudentPromotionSelect = (id) => {
    setSelectedPromotionStudentIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleExecutePromotion = async () => {
    if (selectedPromotionStudentIds.length === 0) return;
    setIsPromoting(true);
    setError('');
    try {
      const promotionsPayload = selectedPromotionStudentIds.map(stId => ({
        student_id: stId,
        to_class_id: targetClassMap[stId]
      })).filter(item => item.to_class_id);

      if (promotionsPayload.length === 0) {
        setError('Please select target class for all checked students.');
        setIsPromoting(false);
        return;
      }

      const res = await api.post('/students/promotion/promote', {
        promotions: promotionsPayload,
        promotion_type: promotionType,
        academic_year: academicYear,
        execution_mode: executionMode,
        effective_date: executionMode === 'scheduled' ? effectiveDate : null
      });

      setSuccessMessage(res.data.message || 'Students promoted successfully!');
      setIsPromoteModalOpen(false);
      fetchStudents();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to promote students');
    } finally {
      setIsPromoting(false);
    }
  };

  const handleCancelScheduledPromotion = async (promotionId) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled promotion?')) return;
    try {
      await api.delete(`/students/promotion/scheduled/${promotionId}`);
      setSuccessMessage('Scheduled promotion cancelled successfully!');
      fetchStudents();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel scheduled promotion');
    }
  };

  // Load branch classes for filter whenever branch changes or page loads for center manager
  useEffect(() => {
    const activeBranchId = user?.role === 'admin'
      ? selectedBranch
      : (currentBranch?.branch_id || user?.branch_id || selectedBranch);

    if (activeBranchId) {
      loadBranchClassesForFilter(activeBranchId);
    } else {
      setFilterClasses([]);
      setSelectedClass('');
    }
  }, [selectedBranch, user, currentBranch]);

  async function loadBranchClassesForFilter(branchId) {
    setFilterClassesLoading(true);
    try {
      const res = await api.get(`/branches/${branchId}/classes`);
      const activeClasses = (res.data.data || []).filter(c => c.is_active === 1);
      setFilterClasses(activeClasses);
    } catch (err) {
      setFilterClasses([]);
    } finally {
      setFilterClassesLoading(false);
    }
  }

  useEffect(() => {
    fetchBranches();
  }, []);

  // Fetch branch classes whenever modal branch_id changes
  useEffect(() => {
    if (form.branch_id) {
      loadBranchClassesForStudent(form.branch_id);
    } else {
      setFormClasses([]);
      setFormClassesLoading(false);
    }
  }, [form.branch_id]);

  async function loadBranchClassesForStudent(branchId) {
    setFormClassesLoading(true);
    try {
      const res = await api.get(`/branches/${branchId}/classes`);
      const loadedClasses = (res.data.data || []).filter(c => c.is_active === 1);
      setFormClasses(loadedClasses);

      if (editingStudent && loadedClasses.length > 0) {
        const currentGrade = form.class_grade || editingStudent.class_grade;
        if (currentGrade) {
          const matched = loadedClasses.find(c => {
            const fullName = c.section ? `${c.name} (${c.section})` : c.name;
            return fullName.trim().toLowerCase() === String(currentGrade).trim().toLowerCase() ||
                   c.name.trim().toLowerCase() === String(currentGrade).trim().toLowerCase();
          });
          if (matched) {
            setForm(p => ({
              ...p,
              class_id: String(matched.id),
              class_grade: matched.section ? `${matched.name} (${matched.section})` : matched.name
            }));
          }
        }
      }
    } catch (err) {
      setFormClasses([]);
    } finally {
      setFormClassesLoading(false);
    }
  }


  useEffect(() => {
    if (isAddModalOpen && !editingStudent) {
      const targetBranchId = user?.role === 'admin'
        ? (form.branch_id || currentBranch?.branch_id || selectedBranch || (branches.length > 0 ? String(branches[0].id) : ''))
        : (currentBranch?.branch_id ? String(currentBranch.branch_id) : form.branch_id);
      
      setForm(p => ({
        ...p,
        branch_id: targetBranchId
      }));
      loadParents(targetBranchId);
    }
  }, [isAddModalOpen, editingStudent, currentBranch, selectedBranch, branches, user]);

  useEffect(() => {
    fetchStudents();
  }, [selectedBranch, selectedClass]);

  async function fetchBranches() {
    try {
      const res = await api.get('/admin/branches');
      setBranches(res.data.data || []);
    } catch (err) {}
  }

  async function loadParents(branchId) {
    try {
      const params = { role: 'parent' };
      if (branchId) params.branch_id = branchId;
      const res = await api.get('/users', { params });
      setParents(res.data.data || []);
    } catch (err) {
      setParents([]);
    }
  }

  async function fetchStudents() {
    try {
      const params = {};
      const activeBranchId = user?.role === 'admin' 
        ? selectedBranch 
        : (currentBranch?.branch_id || user?.branch_id || selectedBranch);

      if (activeBranchId) params.branch_id = activeBranchId;
      if (selectedClass) params.class_id = selectedClass;

      const res = await api.get('/students', { params });
      setStudents(res.data.data || []);
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.message;
      setError(status ? `Error ${status}: ${msg}` : `Network error: ${msg}`);
    }
  }

  const openAddModal = () => {
    setEditingStudent(null);
    setProfileImageFile(null);
    setProfileImagePreview('');
    const initialBranchId = user?.role === 'admin'
      ? (currentBranch?.branch_id || selectedBranch || (branches.length > 0 ? String(branches[0].id) : ''))
      : (currentBranch?.branch_id ? String(currentBranch.branch_id) : '');

    if (initialBranchId) {
      setFormClassesLoading(true);
    }
    setForm({
      ...emptyStudent,
      branch_id: initialBranchId
    });
    loadParents(initialBranchId);
    setIsAddModalOpen(true);
  };

  const openEditModal = (student) => {
    setEditingStudent(student);
    setProfileImageFile(null);
    setProfileImagePreview(student.profile_image || '');
    const targetBranch = String(student.branch_id || '');
    if (targetBranch) {
      setFormClassesLoading(true);
    }
    setForm({
      full_name: student.full_name || '',
      email: student.email || '',
      parent_id: '',
      date_of_birth: student.date_of_birth ? String(student.date_of_birth).split('T')[0] : '',
      date_of_admission: student.date_of_admission ? String(student.date_of_admission).split('T')[0] : '',
      gender: student.gender || '',
      class_grade: student.class_grade || '',
      class_id: student.class_id ? String(student.class_id) : '',
      branch_id: targetBranch,
      blood_group: student.blood_group || '',
      height: student.height || '',
      weight: student.weight || '',
      primary_language: student.primary_language || '',
      is_active: Number(student.is_active) === 1,
    });
    loadParents(targetBranch);
    setIsAddModalOpen(true);
  };

  const closeModal = () => {
    setIsAddModalOpen(false);
    setEditingStudent(null);
    setProfileImageFile(null);
    setProfileImagePreview('');
    setForm(emptyStudent);
  };

  const handleModalBranchChange = (newBranchId) => {
    setForm(p => ({ ...p, branch_id: newBranchId, parent_id: '', class_id: '' }));
    loadParents(newBranchId);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    const targetBranchId = user?.role === 'admin' ? form.branch_id : (currentBranch?.branch_id || form.branch_id);

    if (!targetBranchId) {
      setError('Please select a branch');
      return;
    }

    if (formClasses.length === 0) {
      setError('Selected branch has no classes created yet. Please add classes to this branch from the Branches page first.');
      return;
    }

    if (formClasses.length > 0 && !form.class_id) {
      setError('Please select a class for the student.');
      return;
    }

    if (form.date_of_birth) {
      const maxDob = getMaxDobDate();
      if (form.date_of_birth > maxDob) {
        setError('Student must be at least 2 years old (Date of birth cannot be in future or less than 2 years old)');
        return;
      }
    }

    try {
      const formData = new FormData();
      formData.append('full_name', form.full_name.trim());
      if (form.date_of_birth) formData.append('date_of_birth', form.date_of_birth);
      if (form.date_of_admission) formData.append('date_of_admission', form.date_of_admission);
      if (form.gender) formData.append('gender', form.gender);
      if (form.class_grade) formData.append('class_grade', form.class_grade);
      if (form.class_id) formData.append('class_id', form.class_id);
      formData.append('branch_id', targetBranchId);
      if (form.blood_group) formData.append('blood_group', form.blood_group.trim());
      if (form.height) formData.append('height', form.height.trim());
      if (form.weight) formData.append('weight', form.weight.trim());
      if (form.primary_language) formData.append('primary_language', form.primary_language.trim());
      formData.append('is_active', form.is_active ? '1' : '0');

      if (form.parent_id) formData.append('parent_id', form.parent_id);
      else if (form.email && form.email.trim()) formData.append('email', form.email.trim());

      if (profileImageFile) {
        formData.append('profile_image', profileImageFile);
      }

      if (editingStudent) {
        await api.put(`/admin/students/${editingStudent.id}`, formData);
        setSuccessMessage('Student updated successfully');
      } else {
        const res = await api.post('/students', formData);
        setSuccessMessage(res.data.message || 'Student added successfully');
      }

      closeModal();
      fetchStudents();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save student');
    }
  };

  const handleToggleStatus = async (student) => {
    setError('');
    setSuccessMessage('');
    const isCurrentActive = Number(student.is_active) === 1;
    const newStatus = isCurrentActive ? 0 : 1;
    try {
      await api.put(`/admin/students/${student.id}`, { is_active: newStatus });
      setSuccessMessage(`Student "${student.full_name}" is now ${newStatus === 1 ? 'Active' : 'Inactive'}`);
      fetchStudents();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update status');
    }
  };

  const handleSendEmail = async (student) => {
    setError('');
    setSuccessMessage('');
    setSendingEmailId(student.id);

    try {
      const res = await api.post(`/students/${student.id}/send-credentials`);
      setSuccessMessage(res.data.message || 'Credentials emailed successfully to linked parent');
    } catch (err) {
      setError(err.response?.data?.message || 'Please link a parent first');
    } finally {
      setSendingEmailId(null);
    }
  };

  const sortedStudents = sortItems(students, sortOption, 'full_name', 'created_at');

  const filteredStudents = sortedStudents.filter((student) => {
    const isStudentActive = Number(student.is_active) === 1;
    if (statusFilter === 'active' && !isStudentActive) return false;
    if (statusFilter === 'inactive' && isStudentActive) return false;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const matchName = student.full_name?.toLowerCase().includes(q);
      const matchId = student.student_id?.toLowerCase().includes(q);
      const matchClass = student.class_grade?.toLowerCase().includes(q);
      if (!matchName && !matchId && !matchClass) return false;
    }

    return true;
  });

  return (
    <div>
      <PageHeader
        title="Student Management"
        subtitle="Create child profiles and manage student login IDs"
        action={
          <button type="button" className="primary-button" onClick={openAddModal} style={{ whiteSpace: 'nowrap' }}>
            + Add Student
          </button>
        }
      />
      {successMessage && <div className="success-message">{successMessage}</div>}
      {error && <div className="error-message">{error}</div>}

      <div className="page-header-row" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        {user?.role === 'admin' && (
          <select
            value={selectedBranch}
            onChange={(e) => {
              setSelectedBranch(e.target.value);
              setSelectedClass('');
            }}
            style={{
              padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
              fontSize: 14, minWidth: 160, cursor: 'pointer', background: '#ffffff'
            }}
          >
            <option value="">All branches</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        )}

        {/* Classes Filter Dropdown */}
        <select
          value={selectedClass}
          onChange={(e) => setSelectedClass(e.target.value)}
          disabled={user?.role === 'admin' && !selectedBranch}
          style={{
            padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
            fontSize: 14, minWidth: 160,
            cursor: (user?.role === 'admin' && !selectedBranch) ? 'not-allowed' : 'pointer',
            background: (user?.role === 'admin' && !selectedBranch) ? '#f1f5f9' : '#ffffff',
            color: (user?.role === 'admin' && !selectedBranch) ? '#94a3b8' : '#1e293b'
          }}
        >
          <option value="">
            {filterClassesLoading
              ? 'Loading classes...'
              : (user?.role === 'admin' && !selectedBranch ? 'Select branch first' : 'All Classes')}
          </option>
          {filterClasses.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}{c.section ? ` (${c.section})` : ''}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
            fontSize: 14, minWidth: 160, cursor: 'pointer', background: '#ffffff'
          }}
        >
          <option value="all">All Statuses (Active & Inactive)</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
        </select>

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search student by name, ID, class..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '8px 12px 8px 34px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              fontSize: 14,
              minWidth: 200,
              outline: 'none',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              background: '#ffffff'
            }}
          />
          <span style={{ position: 'absolute', left: 10, color: '#94a3b8', fontSize: 13, pointerEvents: 'none' }}>
            🔍
          </span>
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: 8,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#94a3b8',
                fontSize: 14,
                fontWeight: 700
              }}
            >
              ×
            </button>
          )}
        </div>

        <SortFilter sortOption={sortOption} onSortChange={setSortOption} />

        <button
          type="button"
          onClick={() => setIsPromoteModalOpen(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: '#4f46e5', color: '#ffffff', border: 'none',
            padding: '9px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600,
            cursor: 'pointer', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.2)',
            whiteSpace: 'nowrap',
            marginLeft: 'auto'
          }}
        >
          <span>🎓</span> Promote Students
        </button>
      </div>

      {/* Promote Students Modal */}
      <Modal isOpen={isPromoteModalOpen} onClose={() => setIsPromoteModalOpen(false)} title="🎓 Student Class Promotion" maxWidth="860px">
        <div style={{ padding: '4px 0' }}>
          {/* Academic Terms Banner */}
          <div style={{
            background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
            border: '1px solid #c7d2fe', borderRadius: 10, padding: '12px 16px', marginBottom: 16
          }}>
            <div style={{ fontWeight: 700, color: '#3730a3', fontSize: 14, marginBottom: 4 }}>
              📅 Academic Year & Terms Structure
            </div>
            <div style={{ fontSize: 13, color: '#4338ca', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <span><strong>Term 1:</strong> Jan – Apr</span>
              <span><strong>Term 2 (Mid-Year):</strong> June – Aug (26+ mos / 3+ yrs)</span>
              <span><strong>Term 3 (Year-End):</strong> Sept – Dec (New Jan Session)</span>
            </div>
          </div>

          {/* Promotion Configuration Row */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', flex: 1, minWidth: 220 }}>
              Promotion Type
              <select
                value={promotionType}
                onChange={(e) => setPromotionType(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', marginTop: 4, fontSize: 13 }}
              >
                <option value="december_annual">December Annual Promotion (For Jan New Session)</option>
                <option value="june_mid_year">June Mid-Year Promotion (26+ Months / 3+ Years)</option>
              </select>
            </label>

            <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', width: 140 }}>
              Academic Year
              <select
                value={academicYear}
                onChange={(e) => setAcademicYear(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', marginTop: 4, fontSize: 13 }}
              >
                {getDynamicYears().map((yr) => (
                  <option key={yr} value={yr}>
                    {yr}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/* Execution Mode Options (Immediate vs Scheduled) */}
          <div style={{ background: '#f8fafc', padding: 14, borderRadius: 10, border: '1px solid #e2e8f0', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 10 }}>
              Execution Mode (Promotion Scheduling)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 8 }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                background: executionMode === 'scheduled' ? '#eff6ff' : '#ffffff',
                border: executionMode === 'scheduled' ? '2px solid #3b82f6' : '1px solid #cbd5e1',
                padding: '12px 14px', borderRadius: 10, transition: 'all 0.2s ease',
                boxShadow: executionMode === 'scheduled' ? '0 2px 8px rgba(59, 130, 246, 0.12)' : 'none'
              }}>
                <input
                  type="radio"
                  name="executionMode"
                  value="scheduled"
                  checked={executionMode === 'scheduled'}
                  onChange={() => setExecutionMode('scheduled')}
                  style={{ width: 18, height: 18, accentColor: '#2563eb', cursor: 'pointer', flexShrink: 0 }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                    📅 Schedule for New Session
                  </div>
                  <div style={{ fontSize: 11, color: '#475569', marginTop: 2, fontWeight: 500 }}>
                    Effective from {effectiveDate || `${academicYear}-01-01`}
                  </div>
                </div>
              </label>

              <label style={{
                display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                background: executionMode === 'immediate' ? '#f0fdf4' : '#ffffff',
                border: executionMode === 'immediate' ? '2px solid #22c55e' : '1px solid #cbd5e1',
                padding: '12px 14px', borderRadius: 10, transition: 'all 0.2s ease',
                boxShadow: executionMode === 'immediate' ? '0 2px 8px rgba(34, 197, 94, 0.12)' : 'none'
              }}>
                <input
                  type="radio"
                  name="executionMode"
                  value="immediate"
                  checked={executionMode === 'immediate'}
                  onChange={() => setExecutionMode('immediate')}
                  style={{ width: 18, height: 18, accentColor: '#16a34a', cursor: 'pointer', flexShrink: 0 }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                    ⚡ Promote Immediately Today
                  </div>
                  <div style={{ fontSize: 11, color: '#475569', marginTop: 2, fontWeight: 500 }}>
                    Class shifts right now
                  </div>
                </div>
              </label>
            </div>

            {executionMode === 'scheduled' && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: '#ffffff', padding: 8, borderRadius: 6, border: '1px solid #cbd5e1' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>
                  Effective Session Start Date:
                </label>
                <input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
                <span style={{ fontSize: 11, color: '#64748b' }}>
                  (Student remains in current class until this date)
                </span>
              </div>
            )}
          </div>

          {/* Promotion Students List */}
          {promotionLoading ? (
            <div style={{ textAlign: 'center', padding: 30, color: '#64748b' }}>Loading promotion eligibility...</div>
          ) : eligibleStudents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, color: '#64748b', background: '#f8fafc', borderRadius: 8 }}>
              No active students found for promotion under this criteria.
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap', margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={selectedPromotionStudentIds.length === eligibleStudents.filter(s => s.is_eligible && !s.scheduled_promotion).length && eligibleStudents.filter(s => s.is_eligible && !s.scheduled_promotion).length > 0}
                    onChange={(e) => toggleSelectAllPromotion(e.target.checked)}
                    style={{ margin: 0, width: 16, height: 16, accentColor: '#2563eb', cursor: 'pointer' }}
                  />
                  <span>Select All Eligible ({eligibleStudents.filter(s => s.is_eligible && !s.scheduled_promotion).length})</span>
                </label>
                <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                  Selected: <strong>{selectedPromotionStudentIds.length}</strong> student(s)
                </span>
              </div>

              <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #cbd5e1', textTransform: 'uppercase', fontSize: 11, color: '#475569' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'center', width: 40 }}>Select</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Student Name</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Current Class</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center' }}>Age</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Promote To Next Class</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligibleStudents.map(s => (
                      <tr key={s.id} style={{ borderBottom: '1px solid #f1f5f9', background: selectedPromotionStudentIds.includes(s.id) ? '#f0f9ff' : 'transparent' }}>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedPromotionStudentIds.includes(s.id)}
                            onChange={() => toggleStudentPromotionSelect(s.id)}
                          />
                        </td>
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1e293b' }}>
                          {s.full_name}
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>ID: {s.student_id} | {s.branch_name}</div>
                        </td>
                        <td style={{ padding: '8px 12px', color: '#475569' }}>
                          {s.current_class_name ? `${s.current_class_name}${s.current_class_section ? ` (${s.current_class_section})` : ''}` : (s.class_grade || 'None')}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', color: '#475569', whiteSpace: 'nowrap' }}>
                          {(() => {
                            const m = s.age_in_months || 0;
                            const yrs = Math.floor(m / 12);
                            const mos = m % 12;
                            if (yrs === 0) return `${mos} mos`;
                            if (mos === 0) return `${yrs} Yrs (${m} mos)`;
                            return `${yrs} Yrs ${mos} Mos (${m} mos)`;
                          })()}
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <select
                            value={targetClassMap[s.id] || s.recommended_target_class_id || ''}
                            onChange={(e) => setTargetClassMap(prev => ({ ...prev, [s.id]: e.target.value }))}
                            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, width: '100%' }}
                          >
                            <option value="">Select Target Class</option>
                            {(promotionClasses.filter(c => c.branch_id === s.branch_id)).map(c => (
                              <option key={c.id} value={c.id}>
                                {c.name}{c.section ? ` (${c.section})` : ''}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          {(() => {
                            const isPromotedThisYear = s.last_completed_promotion && String(s.last_completed_promotion.academic_year) === String(academicYear);
                            if (s.scheduled_promotion) {
                              return (
                                <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>
                                  📅 Scheduled ({s.scheduled_promotion.to_class_name})
                                </span>
                              );
                            }
                            if (isPromotedThisYear) {
                              return (
                                <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0' }}>
                                  ✅ Promoted ({s.last_completed_promotion.to_class_name})
                                </span>
                              );
                            }
                            if (s.is_eligible) {
                              return (
                                <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>
                                  ✨ Ready
                                </span>
                              );
                            }
                            return (
                              <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#fffbeb', color: '#b45309', border: '1px solid #fef3c7' }}>
                                ⏳ Not Yet Eligible
                              </span>
                            );
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <button
              type="button"
              onClick={() => setIsPromoteModalOpen(false)}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#ffffff', cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={isPromoting || selectedPromotionStudentIds.length === 0}
              onClick={handleExecutePromotion}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: selectedPromotionStudentIds.length === 0 ? '#cbd5e1' : 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)',
                color: '#ffffff', border: 'none',
                padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 700,
                cursor: selectedPromotionStudentIds.length === 0 ? 'not-allowed' : 'pointer',
                boxShadow: selectedPromotionStudentIds.length === 0 ? 'none' : '0 4px 12px rgba(79, 70, 229, 0.35)',
                transition: 'all 0.2s ease'
              }}
            >
              <span>🎓</span>
              <span>{isPromoting ? 'Processing Promotion...' : `Promote ${selectedPromotionStudentIds.length} Selected Student(s)`}</span>
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isAddModalOpen} onClose={closeModal} title={editingStudent ? 'Edit Student' : 'Add Student'}>
        <form onSubmit={handleSubmit}>
          {/* Profile Photo Upload (Optional) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, background: '#f8fafc', padding: 12, borderRadius: 10, border: '1px solid #e2e8f0' }}>
            <StudentAvatarCell src={profileImagePreview} name={form.full_name} size={56} />
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>
                Profile Photo (Optional)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setProfileImageFile(file);
                    setProfileImagePreview(URL.createObjectURL(file));
                  }
                }}
                style={{ fontSize: 13, color: '#475569' }}
              />
            </div>
          </div>

          <label>
            Full name
            <input placeholder="Full name" value={form.full_name} onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))} required />
          </label>

          <label>
            Branch
            {user?.role === 'admin' ? (
              <select
                value={form.branch_id}
                onChange={(e) => handleModalBranchChange(e.target.value)}
                required
              >
                <option value="">Select Branch</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            ) : (
              <div style={{
                padding: '10px 14px',
                background: '#f8fafc',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#334155',
                marginTop: 4
              }}>
                📍 {currentBranch?.name || 'Current Branch'}
              </div>
            )}
          </label>

          <label>
            Link Parent (Optional)
            <select
              value={form.parent_id || ''}
              onChange={(e) => setForm((p) => ({ ...p, parent_id: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: 14, marginTop: 4 }}
            >
              <option value="">Select Parent (Optional)</option>
              {parents.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} {p.email ? `(${p.email})` : p.phone ? `(${p.phone})` : ''}
                </option>
              ))}
            </select>
          </label>
          
          {!form.parent_id && (
            <label>
              Parent / Student Email (Optional — for sending login credentials)
              <input
                type="email"
                placeholder="parent@example.com"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              />
            </label>
          )}

          <div className="form-row">
            <label style={{ flex: 1 }}>
              Date of birth
              <input
                type="date"
                max={getMaxDobDate()}
                value={form.date_of_birth}
                onChange={(e) => setForm((p) => ({ ...p, date_of_birth: e.target.value }))}
              />
            </label>
            <label style={{ flex: 1 }}>
              Date of admission
              <input
                type="date"
                value={form.date_of_admission}
                onChange={(e) => setForm((p) => ({ ...p, date_of_admission: e.target.value }))}
              />
            </label>
          </div>

          <div className="form-row">
            <label style={{ flex: 1 }}>
              Gender
              <select value={form.gender} onChange={(e) => setForm((p) => ({ ...p, gender: e.target.value }))}>
                <option value="">Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </label>
            <label style={{ flex: 1 }}>
              Class / Grade <span style={{ color: '#ef4444' }}>*</span>
              {!form.branch_id ? (
                <select disabled style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed', color: '#94a3b8' }}>
                  <option value="">Select Branch First</option>
                </select>
              ) : formClassesLoading ? (
                <select disabled style={{ backgroundColor: '#f8fafc', cursor: 'wait', color: '#64748b' }}>
                  <option value="">Loading branch classes...</option>
                </select>
              ) : formClasses && formClasses.length > 0 ? (
                <select
                  value={(() => {
                    if (form.class_id) return String(form.class_id);
                    if (form.class_grade && formClasses && formClasses.length > 0) {
                      const matched = formClasses.find(c => {
                        const fullName = c.section ? `${c.name} (${c.section})` : c.name;
                        return fullName.trim().toLowerCase() === String(form.class_grade).trim().toLowerCase() ||
                               c.name.trim().toLowerCase() === String(form.class_grade).trim().toLowerCase();
                      });
                      if (matched) return String(matched.id);
                    }
                    return '';
                  })()}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const foundClass = formClasses.find(c => String(c.id) === String(selectedId));
                    const classNameStr = foundClass ? (foundClass.section ? `${foundClass.name} (${foundClass.section})` : foundClass.name) : '';
                    setForm(p => ({ ...p, class_id: selectedId, class_grade: classNameStr }));
                  }}
                  required
                >
                  <option value="">Select Branch Class</option>
                  {formClasses.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name} {cls.section ? `(${cls.section})` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <div>
                  <select disabled style={{ backgroundColor: '#fef2f2', border: '1px solid #fca5a5', cursor: 'not-allowed', color: '#991b1b', width: '100%' }}>
                    <option value="">No Classes Created for this Branch</option>
                  </select>
                  <span style={{ fontSize: '11px', color: '#dc2626', marginTop: '4px', display: 'block', fontWeight: 500 }}>
                    ⚠️ No active classes found. Please create classes for this branch from the <strong>Branches</strong> page first.
                  </span>
                </div>
              )}
            </label>
          </div>

          <div className="form-row">
            <label style={{ flex: 1 }}>
              Blood Group
              <select
                value={form.blood_group || ''}
                onChange={(e) => setForm((p) => ({ ...p, blood_group: e.target.value }))}
              >
                <option value="">Select Blood Group</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
              </select>
            </label>
            <label style={{ flex: 1 }}>
              Primary Language
              <input placeholder="e.g. English, Spanish" value={form.primary_language} onChange={(e) => setForm((p) => ({ ...p, primary_language: e.target.value }))} />
            </label>
          </div>

          <div className="form-row">
            <label style={{ flex: 1 }}>
              Height
              <input placeholder="e.g. 110 cm" value={form.height} onChange={(e) => setForm((p) => ({ ...p, height: e.target.value }))} />
            </label>
            <label style={{ flex: 1 }}>
              Weight
              <input placeholder="e.g. 18.5 kg" value={form.weight} onChange={(e) => setForm((p) => ({ ...p, weight: e.target.value }))} />
            </label>
          </div>

          <label className="form-checkbox" style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              name="is_active"
              checked={form.is_active}
              onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
              style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#7c3aed' }}
            />
            <span style={{ fontSize: '14px', fontWeight: 600, color: form.is_active ? '#15803d' : '#b91c1c' }}>
              Active Student
            </span>
          </label>

          <div className="modal-actions">
            <button type="button" className="secondary-button" onClick={closeModal}>Cancel</button>
            <button type="submit" className="primary-button">{editingStudent ? 'Update Student' : 'Save Student'}</button>
          </div>
        </form>
      </Modal>



      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Full name</th>
              <th>Student ID</th>
              <th>Class</th>
              <th>Branch</th>
              <th>DOB</th>
              <th>DOA</th>
              <th>Blood Group</th>
              <th>Height / Weight</th>
              <th>Language</th>
              <th>Status</th>
              <th style={{ textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.length === 0 ? (
              <tr>
                <td colSpan="11" style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>
                  No students found for the selected filter.
                </td>
              </tr>
            ) : (
              filteredStudents.map((student) => (
                <tr key={student.id}>
                  <td 
                    style={{ cursor: 'pointer', color: '#2563eb', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 10 }}
                    onClick={() => navigate(`/students/${student.id}`)}
                  >
                    <StudentAvatarCell src={student.profile_image} name={student.full_name} size={32} />
                    <span>{student.full_name || student.name || '-'}</span>
                  </td>
                  <td>{student.student_id || '-'}</td>
                  <td>
                    <div>{student.class_grade || '-'}</div>
                    {student.scheduled_promotion && (
                      <div style={{
                        fontSize: 11, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
                        borderRadius: 12, padding: '2px 8px', display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4
                      }}>
                        <span>📅 Scheduled: <strong>{student.scheduled_promotion.to_class_name}</strong> ({student.scheduled_promotion.effective_date})</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelScheduledPromotion(student.scheduled_promotion.id);
                          }}
                          title="Cancel Scheduled Promotion"
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 700, padding: '0 2px', fontSize: 13 }}
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </td>
                  <td>{student.branch_name || '-'}</td>
                  <td>{student.date_of_birth ? String(student.date_of_birth).split('T')[0] : '-'}</td>
                  <td>{student.date_of_admission ? String(student.date_of_admission).split('T')[0] : '-'}</td>
                  <td>{student.blood_group || '-'}</td>
                  <td>
                    {student.height || student.weight ? `${student.height || '-'} / ${student.weight || '-'}` : '-'}
                  </td>
                  <td>{student.primary_language || '-'}</td>
                  <td>
                    {(() => {
                      const isActive = Number(student.is_active) === 1;
                      return (
                        <button
                          type="button"
                          onClick={() => handleToggleStatus(student)}
                          title={`Click to set as ${isActive ? 'Inactive' : 'Active'}`}
                          style={{
                            border: 'none',
                            borderRadius: '16px',
                            padding: '4px 12px',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            backgroundColor: isActive ? '#dcfce7' : '#fee2e2',
                            color: isActive ? '#15803d' : '#b91c1c',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <span style={{
                            width: '7px',
                            height: '7px',
                            borderRadius: '50%',
                            backgroundColor: isActive ? '#22c55e' : '#ef4444'
                          }} />
                          {isActive ? 'Active' : 'Inactive'}
                        </button>
                      );
                    })()}
                  </td>
                  <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center', flexWrap: 'nowrap' }}>
                      <button
                        type="button"
                        className="secondary-button"
                        style={{ padding: '4px 10px', fontSize: '12px', whiteSpace: 'nowrap' }}
                        onClick={() => openEditModal(student)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        style={{
                          padding: '4px 10px', fontSize: '12px', borderRadius: '6px',
                          border: '1px solid #c7d2fe', backgroundColor: '#e0e7ff', color: '#4338ca',
                          cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4,
                          whiteSpace: 'nowrap'
                        }}
                        disabled={sendingEmailId === student.id}
                        onClick={() => handleSendEmail(student)}
                        title="Send Student ID & Password via Email"
                      >
                        {sendingEmailId === student.id ? 'Sending...' : '✉️ Send Credentials'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
